import _ from "lodash";
import { newCommonFormatCurrency } from "../../../../backend/src/common/services/mathServices";
import moment from "moment-timezone";
import { chartColors } from "../../../../backend/src/common/chart/graphServices";
import { dummyAccountingResultsData } from "./dummyData";
import {
    chartsThemeSettingsOptions,
    generalThemeSettingsOptions,
    tablesThemeSettingsOptions,
} from "./themesOptions";
import {
    getAccountingPeriodEndMonths,
    getAccountingPeriodStartMonths,
} from "@/services/accountingPeriods";
import { AccountingPeriod } from "../../../../types";

export enum LayoutMode {
    grid,
    free,
}

interface ContainerInterface {
    getChildById(id: any): Box;
    getGridCelldById(id: any): GridCell;
    removeChildById(id: any): void;
    addBox(box: Box): void;
    moveBoxToFront(box: Box);
    moveBoxToBack(box: Box);
    moveBoxForward(box: Box);
    moveBoxBackward(box: Box);
    get offsetWithLayout(): { x: number; y: number };
    get allContainersMap(): Map<string, ContainerInterface>;
}

export class Layout implements ContainerInterface {
    id: string = "grid";
    readonly x: number = 0;
    readonly y: number = 0;
    mode: LayoutMode = LayoutMode.grid;
    width: number; //| string = "100%"
    height: number; //| string = "100%"
    nRows: number = 4;
    nCols: number = 4;
    cellWidthPx: number = 50;
    cellHeightPx: number = 50;

    children: Box[] = [];

    gridCells: GridCell[] = [];

    get gridCellsMap(): { [key: string]: GridCell } {
        return _.keyBy(this.gridCells, "id");
    }

    get childrenMap(): { [key: string]: Box } {
        return _.keyBy(this.children, "id");
    }

    getChildById(id: any) {
        return this.childrenMap?.[id];
    }

    getGridCelldById(id: any) {
        return this.gridCellsMap?.[id];
    }

    removeChildById(id: any) {
        // _.remove(this.children, { id })
        this.children = this.children.filter((child) => child.id !== id);
    }

    constructor(
        mode: LayoutMode = null,
        width: number,
        height: number,
        nRows: number,
        nCols: number,
    ) {
        this.mode = mode;
        this.width = width;
        this.height = height;
        this.nRows = nRows;
        this.nCols = nCols;

        if (this.mode === LayoutMode.grid) {
            this.buildGrid();
        }

        // this.children.push(
        //     new Box("box1", 0, 0, 100, 100, null, null),
        //     new Box("boxtt", 200, 200, 200, 200, null, null)
        // )
    }

    get offsetWithLayout(): { x: number; y: number } {
        return { x: 0, y: 0 };
    }

    get gridCellWidth() {
        let val = this.width;
        // if (typeof this.width === "string" && this.width.trim().endsWith("%")) {
        //     val = parseFloat(this.width.trim().slice(0, -1))
        //     unit = "%"
        // }
        return parseFloat(val.toString()) / this.nCols;
    }

    get gridCellHeight() {
        let val = this.height;
        // if (typeof this.height === "string" && this.height.trim().endsWith("%")) {
        //     val = parseFloat(this.height.trim().slice(0, -1))
        //     unit = "%"
        // }
        return parseFloat(val.toString()) / this.nRows;
    }

    public buildGrid() {
        this.gridCells = [];
        if (this.nRows && this.nCols) {
            for (let col = 0; col < this.nCols; col++) {
                const x = col * this.gridCellWidth;
                for (let row = 0; row < this.nRows; row++) {
                    // const id = "cell_" + col + "_" + row
                    const y = row * this.gridCellHeight;
                    this.gridCells.push(
                        new GridCell(x, y, this.gridCellWidth, this.gridCellHeight, this),
                    );
                }
            }
        } else {
            let cWidth = 0;
            let cHeight = 0;
            while (cWidth < this.width) {
                while (cHeight < this.height) {
                    this.gridCells.push(
                        new GridCell(cWidth, cHeight, this.cellWidthPx, this.cellHeightPx, this),
                    );
                    cHeight += this.cellHeightPx;
                }
                cWidth += this.cellWidthPx;
                cHeight = 0;
            }
        }
    }

    public setWidth(width: number) {
        this.width = width;
        this.buildGrid();
    }

    public setHeight(height: number) {
        this.height = height;
        this.buildGrid();
    }

    public setWidthAndHeight(width: number, height: number) {
        this.width = width;
        this.height = height;
        this.buildGrid();
    }

    public addBox(box: Box) {
        box.parentContainer = this;
        this.children.push(box);
    }

    public moveBoxToFront(box: Box) {
        if (box?.id in this.childrenMap) {
            this.children = this.children.filter((child) => child.id !== box.id);
            this.children.push(box);
        }
    }

    public moveBoxToBack(box: Box) {
        if (box?.id in this.childrenMap) {
            this.children = this.children.filter((child) => child.id !== box.id);
            this.children.unshift(box);
        }
    }

    public moveBoxForward(box: Box) {
        if (box?.id in this.childrenMap) {
            const index = this.children.findIndex((child) => child.id === box.id);
            if (index < this.children.length - 1) {
                this.children.splice(index, 1);
                this.children.splice(index + 1, 0, box);
            }
        }
    }

    public moveBoxBackward(box: Box) {
        if (box?.id in this.childrenMap) {
            const index = this.children.findIndex((child) => child.id === box.id);
            if (index > 0) {
                this.children.splice(index, 1);
                this.children.splice(index - 1, 0, box);
            }
        }
    }

    get allContainersMap(): Map<string, ContainerInterface> {
        // const containers = new Map<string, ContainerBox | Layout>()
        // for (const child of this.children) {
        //     if (child instanceof ContainerBox) {
        //         containers[child.id] = child
        //         while (child?.parentContainer) {
        //             if (child.parentContainer instanceof ContainerBox) {
        //                 if (containers.has(child.parentContainer.id)) {
        //                     break
        //                 } else {
        //                     containers[child.parentContainer.id] = child.parentContainer
        //                 }
        //             }
        //         }
        //     }
        // }
        // return containers

        const containers: ContainerInterface[] = [this];
        const visited = new Map<string, boolean>();

        // Depth first search to get all containers
        const dfs = (container: GridContainer | Layout) => {
            visited.set(container.id, true);
            containers.push(container);
            for (const child of container.children) {
                if (child instanceof GridContainer && !visited.has(child.id)) {
                    dfs(child);
                }
            }
        };
        dfs(this);
        return _.keyBy(containers, "id") as any;
    }

    /**
     * List of references of all children and grand children, including containers
     */
    get allObjects(): { depth: number; box: Box }[] {
        const objects: { depth: number; box: Box }[] = [];
        let depth = 0;
        const dfs = (container: GridContainer | Layout) => {
            for (const child of container.children) {
                objects.push({ depth: Math.min(depth, 5), box: child });
                if (child instanceof GridContainer) {
                    depth++;
                    dfs(child);
                    depth--;
                }
            }
        };
        dfs(this);
        return objects;
    }
}

export class GridCell {
    id: any;
    x: number;
    y: number;
    width: number;
    height: number;
    box: Box | null; // Box contained in the cell
    parentContainer: Layout | GridContainer = null; // Direct container of the cell

    _generateId() {
        return "cell_" + Math.random().toString(36).substring(2, 17);
    }

    constructor(
        x: number,
        y: number,
        width: number,
        height: number,
        parentContainer: Layout | GridContainer,
    ) {
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;
        this.parentContainer = parentContainer;
        this.id = this._generateId();
    }

    setCoordinates(x: number, y: number) {
        this.x = x;
        this.y = y;
    }

    clone(): GridCell {
        return new GridCell(this.x, this.y, this.width, this.height, this.parentContainer);
    }
}

export class Box {
    id: any;
    x: number;
    y: number;
    name: string = "";
    friendlyTypename: string = "Boite générique";
    // rx: number = 0
    // ry: number = 0
    width_: number;
    height_: number;

    protected previousX: number = 0; // Previous x position of the container
    protected previousY: number = 0; // Previous y position of the container
    protected previousWidth: number = 0; // Previous width of the container
    protected previousHeight: number = 0; // Previous height of the container

    properties: any = {}; // Properties set during component design
    reportProperties: any = {}; // Properties set only when used in report
    // propertiesOptions: any = {}
    widthSnap: number = 1;
    heightSnap: number = 1;
    xSnap: number = 1;
    ySnap: number = 1;
    useSnapping: boolean = true;
    children: Box[] = [];
    themeSettings: any = null;

    resizePointHeight = 8;
    resizePointWidth = 8;

    minWidth: number = 50;
    minHeight: number = 50;

    editableContent: boolean = false; // When set to true, the content the mousedown event will not prevent default, thus allowing content selection and edition (needs to be implemented)

    parentContainer: Layout | GridContainer = null; // Direct container of the box
    layout: Layout | ReportElement = null; // Layout manager

    hideOverflowContent: boolean = true;

    isSelected: boolean = false; // Set to true when box is selected in the editor

    cell: GridCell = null; // GridCell that contains the box
    visible: boolean = true; // Set to false when box is hidden in the editor

    get propertiesOptions() {
        return {};
    }

    get reportPropertiesOptions(): { [key: string]: any } {
        return {};
    }

    get themeSettingsOptions() {
        return {
            general: generalThemeSettingsOptions,
            charts: chartsThemeSettingsOptions,
            tables: tablesThemeSettingsOptions,
        };
    }

    get selectedGeneralTheme() {
        const val = this.themeSettings?.general?.themesList?.[0]; //?.find(theme => theme.id === this.properties.chartTheme)
        console.log("val", val);
        return val;
    }

    get selectedChartTheme() {
        const val = this.themeSettings?.charts?.themesList?.find(
            (theme) => theme.id === this.properties.chartTheme,
        );
        console.log("val", val);
        return val;
    }

    get selectedTableTheme() {
        return this.themeSettings?.tables?.themesList?.find(
            (theme) => theme.id === this.properties.tableTheme,
        );
    }

    /**
     * Style applied to the box. The style is initially set in the general theme options.
     * However, it can be overridden by the box.style object
     */
    get boxStyle() {
        const styleInTheme: any = this.selectedGeneralTheme?.values?.boxStyle ?? {};
        const styleCopy: any = JSON.parse(JSON.stringify(this.style ?? {}));
        if (styleCopy.boxTitle) {
            delete styleCopy.boxTitle;
        }
        const merged: any = {};
        for (const key in styleInTheme) {
            merged[key] =
                styleCopy[key] || styleCopy[key] === 0 || styleCopy[key] === false
                    ? styleCopy[key]
                    : styleInTheme[key];
        }
        return merged;
    }

    /**
     * Style applied to the title in the box. The style is initially set in the general theme options.
     * However, it can be overridden by the box.style object
     */
    get boxTitleStyle() {
        const styleInTheme: any = this.selectedGeneralTheme?.values?.boxTitleStyle ?? {};
        const styleCopy: any = this.style?.boxTitle ?? {};
        const merged: any = {};
        for (const key in styleInTheme) {
            merged[key] =
                styleCopy[key] || styleCopy[key] === 0 || styleCopy[key] === false
                    ? styleCopy[key]
                    : styleInTheme[key];
        }
        return merged;
    }

    style = {
        fill: "",
        border: "",
        stroke: "",
        borderRadius: null,
        boxTitle: {
            color: "",
            fill: "",
            border: "",
            stroke: "",
            borderRadius: null,
            bold: null,
        },
    };

    styleOptions = {
        fill: {
            type: "color",
            label: "Remplissage",
        },
        border: {
            type: "boolean",
            label: "Bordure",
        },
        stroke: {
            type: "color",
            label: "Bordure",
            enableConditions: [{ key: "border", value: true }],
        },
        borderRadius: {
            type: "number",
            label: "Rayon bordure",
            min: 0,
            step: 0.5,
        },
        boxTitle: {
            color: {
                type: "color",
                label: "Couleur du texte",
            },
            fill: {
                type: "color",
                label: "Remplissage",
            },
            border: {
                type: "boolean",
                label: "Bordure",
            },
            stroke: {
                type: "color",
                label: "Bordure",
                enableConditions: [{ key: "border", value: true }],
            },
            borderRadius: {
                type: "number",
                label: "Rayon bordure",
                min: 0,
                step: 0.5,
            },
            bold: {
                type: "boolean",
                label: "Texte en gras",
            },
        },
    };

    roundToSnap(val: number, snap: number) {
        return val; //Math.round(val / snap) * snap
    }

    updatePreviousCoordinates(): void {
        this.previousX = this.x;
        this.previousY = this.y;
        this.previousWidth = this.width;
        this.previousHeight = this.height;
    }

    get title() {
        return this.properties?.title;
    }

    set title(val: string) {
        this.properties.title = val;
    }

    _generateId() {
        return "box_" + Math.random().toString(36).substring(2, 17);
    }

    get width() {
        return this.width_;
    }

    set width(val: number) {
        this.width_ = Math.max(val, this.minWidth); // this.useSnapping ? this.roundToSnap(Math.abs(val), this.widthSnap) : Math.abs(val)
    }

    get height() {
        return this.height_;
    }

    set height(val: number) {
        this.height_ = Math.max(val, this.minHeight); // this.useSnapping ? this.roundToSnap(Math.abs(val), this.heightSnap) : Math.abs(val)
    }

    get absLeftResizePoint() {
        return {
            x: this.x - this.resizePointWidth / 2,
            y: this.y + this.height / 2,
        };
    }

    get absRightResizePoint() {
        return {
            x: this.x + this.width - this.resizePointWidth / 2,
            y: this.y + this.height / 2,
        };
    }

    get absTopResizePoint() {
        return {
            x: this.x + this.width / 2,
            y: this.y - this.resizePointHeight / 2,
        };
    }

    get absBottomResizePoint() {
        return {
            x: this.x + this.width / 2,
            y: this.y + this.height - this.resizePointHeight / 2,
        };
    }

    get absTopLeftResizePoint() {
        return {
            x: this.x - this.resizePointWidth / 2,
            y: this.y - this.resizePointHeight / 2,
        };
    }

    get absTopRightResizePoint() {
        return {
            x: this.x + this.width - this.resizePointWidth / 2,
            y: this.y - this.resizePointHeight / 2,
        };
    }

    get absBottomLeftResizePoint() {
        return {
            x: this.x - this.resizePointWidth / 2,
            y: this.y + this.height - this.resizePointHeight / 2,
        };
    }

    get absBottomRightResizePoint() {
        return {
            x: this.x + this.width - this.resizePointWidth / 2,
            y: this.y + this.height - this.resizePointHeight / 2,
        };
    }

    get leftResizePoint() {
        return {
            x: -this.resizePointWidth / 2,
            y: this.height / 2,
        };
    }

    get rightResizePoint() {
        return {
            x: this.width - this.resizePointWidth / 2,
            y: this.height / 2,
        };
    }

    get topResizePoint() {
        return {
            x: this.width / 2,
            y: -this.resizePointHeight / 2,
        };
    }

    get bottomResizePoint() {
        return {
            x: this.width / 2,
            y: this.height - this.resizePointHeight / 2,
        };
    }

    get topLeftResizePoint() {
        return {
            x: -this.resizePointWidth / 2,
            y: -this.resizePointHeight / 2,
        };
    }

    get topRightResizePoint() {
        return {
            x: this.width - 2.5,
            y: -2.5,
        };
    }

    get bottomLeftResizePoint() {
        return {
            x: -2.5,
            y: this.height - 2.5,
        };
    }

    get bottomRightResizePoint() {
        return {
            x: this.width - 2.5,
            y: this.height - 2.5,
        };
    }

    constructor(
        layout: Layout,
        x: number,
        y: number,
        width: number,
        height: number,
        themeSettings: any = null,
    ) {
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;
        this.themeSettings = themeSettings;

        this.id = this._generateId();
        this.layout = layout;
    }

    setCoordinates(x: number, y: number) {
        this.x = x;
        this.y = y;
    }

    setDataItem(key: string, value: any) {
        this.properties[key] = value;
    }

    setDataItemOptions(key: string, options: any) {
        this.propertiesOptions[key] = options;
    }

    /**
     * Clone the box.
     * @param parent parent container that the clone should belong to
     * @param origin true if the cloning was initiated on this box. Containers can be cloned too, in this case, origin is false for the children
     * @returns cloned  box
     */
    clone(parent: GridContainer | Layout = null, origin: boolean = false) {
        // debugger
        const cloned: Box = _.assign(_.create(Object.getPrototypeOf(this)), _.cloneDeep(this));
        cloned.cell = null;
        cloned.id = this._generateId();
        cloned.layout = this.layout;
        cloned.isSelected = false; // Set the selected status to false

        cloned.parentContainer = parent ?? this.layout;
        const offsetWithLayout_ = cloned.offsetWithLayout;
        if (!parent || parent instanceof Layout) {
            // Only move if parent is the layout. Children's coordinates are relative to the parent
            cloned.x = offsetWithLayout_.x + cloned.x + 10;
            cloned.y = offsetWithLayout_.y + cloned.y + 10;
        }

        if (origin) {
            // Place the newly cloned box next to his original, wrt the layout
            const originalBoxLayoutOffset = this.offsetWithLayout;
            // cloned.parentContainer = this.parentContainer
            cloned.x += originalBoxLayoutOffset.x;
            cloned.y += originalBoxLayoutOffset.y;
        }
        if (cloned instanceof GridContainer && this instanceof GridContainer) {
            // cloned.children.forEach(child => child.id = child._generateId())
            cloned.children = this.children.map((child) => child.clone(cloned, false));
            // cloned.children.forEach(child => child.parentContainer = cloned)
            cloned.children.forEach((child) => (child.layout = this.layout));
            cloned.gridCells = this.gridCells.map((cell) => cell.clone());

            const oldChildIdToNewChildMap = new Map<string, Box>(); // Mapping original child id with the clone child
            for (let i = 0; i < this.children.length; i++) {
                oldChildIdToNewChildMap.set(this.children[i].id, cloned.children[i]);
            }

            let childrenCounter = 0;
            // We to update the references between the cloned children and the cloned cells
            for (let i = 0; i < this.gridCells.length; i++) {
                if (this.gridCells[i].box) {
                    cloned.gridCells[i].box = oldChildIdToNewChildMap.get(this.gridCells[i].box.id);
                    cloned.children[childrenCounter].cell = cloned.gridCells[i];
                    childrenCounter++;
                }
                // cloned.gridCells[i].box = cloned.children[i]
                // cloned.children[i].cell = cloned.gridCells[i]
            }
            cloned.buildGrid(/*true*/);
        }
        cloned.updatePreviousCoordinates();
        return cloned;
    }

    get offsetWithParent() {
        return {
            x: this.x - this.parentContainer.x,
            y: this.y - this.parentContainer.y,
        };
    }

    get offsetWithLayout() {
        return this.offsetsWithAncestor(this.layout);
    }

    offsetsWithAncestor(ancestor: Layout | GridContainer) {
        let val = {
            x: 0,
            y: 0,
        };
        let parent: Layout | GridContainer = this.parentContainer;
        while (parent && parent !== ancestor) {
            val.x += parent.x;
            val.y += parent.y;
            if (parent instanceof Layout) {
                break;
            }
            parent = parent.parentContainer; //instanceof ContainerBox ? parent.parentContainer : null
        }
        return val;
    }
}

export class ComponentBoxModel extends Box {
    type: string = "component";
    componentName: string;
    props: any;
    friendlyTypename = "Widget";

    get tag() {
        return this.componentName;
    }

    constructor(
        layout: Layout,
        x: number,
        y: number,
        width: number,
        height: number,
        themeSettings: any = null,
    ) {
        super(layout, x, y, width, height, themeSettings);
    }
}

export class TextFieldBox extends ComponentBoxModel {
    editableContent = true;
    type = "basic";
    componentName = "TextField";
    friendlyTypename = "Champ de texte";
    properties = {
        text: "Test",
        textStyle: "normal",
    };

    reportProperties: any = this.properties;

    get propertiesOptions() {
        const textStyleChoices = [{ value: "normal", text: "Normal" }];

        Object.keys(this.themeSettingsOptions.general).forEach((key) => {
            if (this.themeSettingsOptions.general[key].type === "TEXT_STYLE") {
                textStyleChoices.push({
                    value: key,
                    text: this.themeSettingsOptions.general[key].label,
                });
            }
        });

        return {
            text: {
                type: "text",
                label: "Texte",
                visible: false, // TODO set to false
            },
            textStyle: {
                type: "select",
                label: "Style",
                choices: textStyleChoices,
            },
        };
    }

    get selectedTextStyle() {
        return this.selectedGeneralTheme?.values?.[this.properties.textStyle] ?? {};
    }

    constructor(
        layout: Layout,
        x: number,
        y: number,
        width: number,
        height: number,
        themeSettings: any = null,
    ) {
        super(layout, x, y, width, height, themeSettings);
    }

    get reportPropertiesOptions() {
        return {
            ...this.propertiesOptions,
            textStyle: {
                visible: false,
            },
        };
    }
}

export class TitleBox extends TextFieldBox {
    editableContent = false;
    componentName = "Title";
    friendlyTypename = "Titre";
    properties = {
        text: "Titre",
        textStyle: "h1",
    };

    reportProperties: any = this.properties;

    get reportPropertiesOptions(): any {
        return {
            ...super.reportPropertiesOptions,
            text: {
                type: "text",
                label: "Texte",
                visible: true,
            },
        };
    }
}

export class IndicatorComponentBoxModel extends ComponentBoxModel {
    componentName: string = "Indicator";
    bobbeeCardTag = "";
    bobbeeCardProps: any = {};
    friendlyTypename = "Indicateur";

    minWidth = 100;
    minHeight = 100;

    data_: any = null;

    get cardProps() {
        return this.bobbeeCardProps;
    }

    properties: any = {
        // indicator: "results",
        // title: "Indicateur",
        ui: "table",
        // chartType: "bar",
        chartTheme: "default",
        tableTheme: "default",
        // colorPalette: "cool",
        dense: false,
        // id: "",
        nm1: false,
        cumulative: false,
        // period: [],
    };

    reportProperties: { [key: string]: any } = {
        indicator: "results",
        title: "Indicateur",
        period: [],
    };

    get mergedProperties(): { [key: string]: any } {
        const obj = { ...this.properties };
        Object.keys(this.reportProperties).forEach((key) => {
            if (this.reportProperties[key] != null && this.reportProperties[key] !== "") {
                obj[key] = this.reportProperties[key];
            } else if (obj[key] == null) {
                obj[key] = this.reportProperties[key];
            }
        });

        return obj;

        // return {...this.properties, ...this.reportProperties}
    }

    get propertiesOptions(): any {
        return {
            indicator: {
                type: "select",
                label: "Indicateur",
                choices: [
                    { value: "results", text: "Résultats" },
                    { value: "ca", text: "Chiffre d'affaire" },
                    { value: "expenses", text: "Charges" },
                ],
            },
            title: {
                type: "text",
                label: "Titre de l'indicateur",
            },
            ui: {
                type: "select",
                label: "Type d'indicateur",
                choices: [
                    { value: "chart", text: "Graphe" },
                    { value: "table", text: "Tableau" },
                    { value: "card", text: "Carte" },
                ],
            },
            // chartType: {
            //     type: "select",
            //     label: "Type de graphique",
            //     choices: [
            //         { value: "bar", text: "Barres" },
            //         { value: "line", text: "Lignes" },
            //         { value: "pie", text: "Secteurs" },
            //         { value: "doughnut", text: "Doughnut" }

            //     ],
            //     prerequisites: [
            //         { key: "ui", value: "chart" }
            //     ]
            // },
            chartTheme: {
                type: "select",
                label: "Thème des graphiques",
                choices:
                    this.themeSettings?.charts?.themesList?.map((theme) => ({
                        value: theme.id,
                        text: theme.displayName,
                    })) || [],
                prerequisites: [{ key: "ui", value: "chart" }],
            },
            tableTheme: {
                type: "select",
                label: "Thème des tableaux",
                choices:
                    this.themeSettings?.tables?.themesList?.map((theme) => ({
                        value: theme.id,
                        text: theme.displayName,
                    })) || [],
                prerequisites: [{ key: "ui", value: "table" }],
            },
            // colorPalette: {
            //     type: "select",
            //     label: "Palette de couleurs",
            //     choices: [
            //         { value: "cool", text: "Cool" },
            //         { value: "warm", text: "Chaleureux" },
            //         { value: "neon", text: "Néon" }
            //     ],
            //     prerequisites: [
            //         { key: "ui", value: "chart" }
            //     ]
            // },
            // id: {
            //     type: "text",
            //     label: "Chart ID"
            // },
            nm1: {
                type: "boolean",
                label: "N-1",
                prerequisites: [{ key: "ui", value: ["table", "chart"] }],
            },
            cumulative: {
                type: "boolean",
                label: "Cumulé",
                prerequisites: [{ key: "ui", value: ["table", "chart"] }],
            },
            dense: {
                type: "boolean",
                label: "Compact",
                prerequisites: [{ key: "ui", value: ["table", "chart"] }],
            },
            period: {
                type: "date-picker",
                unit: "month",
                label: "Période",
                min: "2024-01",
                max: "2024-12",
                prerequisites: [
                    // { key: "ui", value: ["table", "chart"] }
                ],
            },
        };
    }

    get reportPropertiesOptions() {
        return {
            indicator: {
                type: "select",
                label: "Indicateur",
                choices: [
                    { value: "results", text: "Résultats" },
                    { value: "ca", text: "Chiffre d'affaire" },
                    { value: "expenses", text: "Charges" },
                ],
            },
            title: {
                type: "text",
                label: "Titre de l'indicateur",
            },
            period: {
                type: "date-picker",
                unit: "month",
                label: "Période",
                min: "2024-01",
                max: "2024-12",
            },
        };
    }

    constructor(
        layout: Layout,
        x: number,
        y: number,
        width: number,
        height: number,
        bobbeeCardTag,
        bobbeeCardProps,
        data = null,
        themeSettings: any = null,
    ) {
        super(layout, x, y, width, height, themeSettings);
        this.bobbeeCardTag = bobbeeCardTag;
        this.bobbeeCardProps = bobbeeCardProps;

        this.data = {
            dataset: [
                [
                    14321.5, 13765.3, 12897.7, 14623.8, 13412.9, 14873.4, 13521.6, 14215.2, 13874.0,
                    14026.7, 13942.6, 13503.5,
                ],
                [
                    23456.2, 22890.7, 22678.1, 23145.8, 22793.6, 23001.5, 22988.3, 23412.9, 23115.7,
                    22876.4, 22956.2, 23083.8,
                ],
            ],
            labels: [
                "01-2024",
                "02-2024",
                "03-2024",
                "04-2024",
                "05-2024",
                "06-2024",
                "07-2024",
                "08-2024",
                "09-2024",
                "10-2024",
                "11-2024",
                "12-2024",
            ],
        };
    }

    set data(data: any) {
        this.data_ = data;
    }

    get data() {
        return this.data_;
    }

    get chartOptions() {
        // const dummyGraphOptions = {
        //     responsive: true,
        //     maintainAspectRatio: false,
        //     scales: {
        //         y: {
        //             beginAtZero: true
        //         }
        //     }
        // }
        const options = {
            responsive: true,
            plugins: {
                legend: {
                    display:
                        (this.data.dataset?.length ?? 0) > 1 &&
                        this.mergedProperties.nm1 &&
                        !this.mergedProperties.dense,
                    position: "bottom",
                },
            },
            scales: {
                y: {
                    stacked: false,
                    // min:0,
                    beginAtZero: true,
                    // stepSize: 1
                },
                x: {
                    stacked: false,
                    // min:0,
                    beginAtZero: true,
                },
            },
            maintainAspectRatio: true,
        };
        return options;
    }

    get chartData() {
        // const dummyGraphData = {
        //     "labels": [
        //         "N-1",
        //         "N"
        //     ],
        //     "datasets": [
        //         {
        //             "label": "netAmounts",
        //             "data": [
        //                 165978.2,
        //                 275199.2530043432
        //             ],
        //             "backgroundColor": "rgba(56,144,175,1)",
        //             "borderColor": "rgba(255,255,255,0)"
        //         },
        //         // {
        //         //     "label": "netAmounts2",
        //         //     "data": [
        //         //         165978.2,
        //         //         275199.2530043432
        //         //     ],
        //         //     "backgroundColor": "rgba(6,14,175,1)",
        //         //     "borderColor": "rgba(25,25,255,0)"
        //         // }
        //     ]
        // }
        const data: any = {};

        if (this.mergedProperties.dense) {
            data.labels = this.mergedProperties.nm1 ? ["N-1", "N"] : ["N"];
            // const totals = this.selectedData.rows.map(row => row.reduce((total, x) => total + x, 0))
            const datasets = [
                {
                    data: this.selectedData.rows,
                },
            ];
            data.datasets = datasets;
        } else {
            data.labels = this.selectedData.header;
            // const datasets = [
            //     {
            //         data
            //     }
            // ]
            const datasets = this.selectedData.rows.map((row, i) => {
                return {
                    // label: this.data.labels[i],
                    data: row,
                };
            });

            data.datasets = datasets;
            if (data.datasets.length === 2 && this.mergedProperties.nm1) {
                // The data has at most two rows
                data.datasets[0].label = "N-1";
                data.datasets[1].label = "N";
            } else if (
                data.datasets.length === 1 ||
                (data.datasets.length === 2 && !this.mergedProperties.nm1)
            ) {
                data.datasets[0].label = "N";
            }
        }
        const chartType = this.selectedChartTheme ? this.selectedChartTheme.values.type : "bar";
        const colorPalette = this.selectedChartTheme
            ? this.selectedChartTheme.values.palette
            : "cool";
        switch (chartType) {
            case "line":
                chartColors(chartType, data.datasets, colorPalette);
                break;
            case "pie":
                chartColors(chartType, data.datasets[0], colorPalette);
                break;
            case "doughnut":
                chartColors(chartType, data.datasets[0], colorPalette);
                break;
            case "bar":
            default:
                chartColors(chartType, data.datasets, colorPalette);
                break;
        }
        console.log("chartData", data);
        return data;
    }

    get selectedData() {
        let out = { header: [], rows: [] };
        if (!this.mergedProperties.period?.length) {
            out.header = this.data.labels;
            out.rows = this.data.dataset.map((row) => [...row]); //.slice(0, this.mergedProperties.nm1 ? 2 : 1)
            // return {
            //     header: this.data.labels,
            //     rows: this.data.dataset.map((row) => row).slice(0, this.mergedProperties.nm1 ? 2 : 1)
            // }
        } else {
            const range = [...this.mergedProperties.period];
            range.sort((a, b) => `${a}`.localeCompare(b));

            let date = range[0];
            const dates = [];
            do {
                dates.push(moment(date).format("YYYY-MM"));
                date = moment(date).add(1, "month");
            } while (moment(date).isSameOrBefore(range[range.length - 1]));

            const refStart = moment(this.data.labels[0], "MM-YYYY");
            const monthsIndices = dates.map((x) => moment(x).diff(refStart, "month", true));
            // console.log("range", range, "dates", dates, "monthsIndices", monthsIndices, "refStart", refStart)
            this.data.dataset.map((row) => console.log("row", row));
            out.header = dates;
            out.rows = this.data.dataset.map((row) => [...monthsIndices.map((i) => row[i])]);
            // return {
            //     header: dates,
            //     rows: this.data.dataset.map(row => monthsIndices.map(i => row[i]))
            // }
        }
        if (!this.mergedProperties.nm1) {
            out.rows.shift();
        }
        if (this.mergedProperties.dense) {
            out.rows = out.rows.map((row) => row.reduce((total, x) => total + x, 0));
        }

        if (this.mergedProperties.cumulative) {
            const cumRows: [][] = [];
            let isListOfArrays = false;
            for (const row of out.rows) {
                if (Array.isArray(row)) {
                    isListOfArrays = true;
                    for (let i = 1; i < row.length; i++) {
                        (row[i] as any) += row[i - 1];
                    }
                }
            }
            if (!isListOfArrays) {
                for (let i = 1; i < out.rows.length; i++) {
                    out.rows[i] += out.rows[i - 1];
                }
            }
        }
        return out;
    }

    get tableData() {
        // const tableData1 = {  // TODO: Just test data. Implement logic
        //     header: ["", "N-1", "N", "Ecart"],
        //     rows: [
        //         ["Total", newCommonFormatCurrency(165978.2), newCommonFormatCurrency(275199.2530043432), newCommonFormatCurrency(275199.2530043432 - 165978.2)],
        //     ]
        // }

        // const tableData2 = { // TODO: Just test data. Implement logic
        //     header: ["", "", "01-2024", "02-2024", "03-2024", "04-2024", "05-2024", "06-2024", "07-2024", "08-2024", "09-2024",
        //         "10-2024", "11-2024", "12-2024"],
        //     rows: [
        //         ["N", "", ...([23456.2, 22890.7, 22678.1, 23145.8, 22793.6, 23001.5, 22988.3, 23412.9, 23115.7, 22876.4, 22956.2, 23083.8].map(x => newCommonFormatCurrency(x)))],
        //         ["N-1", "", ...([14321.5, 13765.3, 12897.7, 14623.8, 13412.9, 14873.4, 13521.6, 14215.2, 13874.0, 14026.7, 13942.6, 13503.5].map(x => newCommonFormatCurrency(x)))]
        //     ]
        // }
        let headers: any = [];
        let rows: any = [];
        // debugger
        if (this.mergedProperties.dense) {
            const selectedRows = this.selectedData.rows;
            headers = this.mergedProperties.nm1 ? ["", "N-1", "N"] : ["", "N"];
            rows = [["Total", ...selectedRows]];
        } else {
            headers = ["", ...this.selectedData.header];
            rows = this.selectedData.rows.map((row, i) => [
                this.mergedProperties.nm1 ? (i === 0 ? "N-1" : "N") : "N",
                ...row,
            ]);
        }
        return {
            headers: headers,
            rows: rows, // List of lists
        };
    }
}

export class RatioComponentBoxModel extends IndicatorComponentBoxModel {
    componentName: string = "RatioIndicator";
    minWidth = 200;
    minHeight = 200;
    accountingPeriod: AccountingPeriod = null;
    friendlyTypename = "Indicateur ratio";

    properties: any = {
        // indicator: "results",
        // title: "Ratio",
        ui: "table",
        // chartType: "bar",
        chartTheme: "default",
        tableTheme: "default",
        // colorPalette: "cool",
        // dense: false,
        // numeratorKey: "",
        // denominatorKey: "",
        nm1: false,
        // cumulative: false,
        // period: [],
        // periodStart: "",
        // periodEnd:  ""
    };

    reportProperties = {
        title: "Ratio",
        numeratorKey: "",
        denominatorKey: "",
        periodStart: "",
        periodEnd: "",
    };

    get propertiesOptions(): any {
        const ratioKeyChoices = [
            { value: "K5M7dlWgTNheYB5E_70", text: "Chiffre d'affaires" },
            { value: "K5M7dlWgTNheYB5E", text: "Chiffre d'affaires (sig)" },
            { value: "HjWAbLWSgQTi5Wei", text: "Charges" },
            { value: "GMmaP08MdztHyvBH", text: "Résultat" },
            { value: "Cj3Em5e2ibqXknwb", text: "Achats consommés" },
        ];
        if (this.mergedProperties.numeratorKey === "") {
            this.reportProperties.numeratorKey = ratioKeyChoices[0].value;
        }
        if (this.mergedProperties.denominatorKey === "") {
            this.reportProperties.denominatorKey = ratioKeyChoices[0].value;
        }

        const periodStartChoices = this.accountingPeriod
            ? getAccountingPeriodStartMonths(this.accountingPeriod)
            : [];
        const periodEndChoices = this.accountingPeriod
            ? getAccountingPeriodEndMonths(this.accountingPeriod)
            : [];
        // debugger
        if (periodStartChoices.length && this.mergedProperties.periodStart === "") {
            this.reportProperties.periodStart = periodStartChoices[0].value;
        }
        if (periodEndChoices.length && this.mergedProperties.periodEnd === "") {
            this.reportProperties.periodEnd = periodEndChoices[periodEndChoices.length - 1].value;
        }

        return {
            title: {
                type: "text",
                label: "Titre de l'indicateur",
            },
            ui: {
                type: "select",
                label: "Type d'indicateur",
                choices: [
                    { value: "chart", text: "Graphe" },
                    { value: "table", text: "Tableau" },
                    { value: "card", text: "Carte" },
                ],
            },
            // chartType: {
            //     type: "select",
            //     label: "Type de graphique",
            //     choices: [
            //         { value: "bar", text: "Barres" },
            //         { value: "line", text: "Lignes" },
            //         { value: "pie", text: "Secteurs" },
            //         { value: "doughnut", text: "Doughnut" }

            //     ],
            //     prerequisites: [
            //         { key: "ui", value: "chart" }
            //     ]
            // },
            chartTheme: {
                type: "select",
                label: "Thème des graphiques",
                choices:
                    this.themeSettings?.charts?.themesList?.map((theme) => ({
                        value: theme.id,
                        text: theme.displayName,
                    })) || [],
                prerequisites: [{ key: "ui", value: "chart" }],
            },
            tableTheme: {
                type: "select",
                label: "Thème des tableaux",
                choices:
                    this.themeSettings?.tables?.themesList?.map((theme) => ({
                        value: theme.id,
                        text: theme.displayName,
                    })) || [],
                prerequisites: [{ key: "ui", value: "table" }],
            },
            // colorPalette: {
            //     type: "select",
            //     label: "Palette de couleurs",
            //     choices: [
            //         { value: "cool", text: "Cool" },
            //         { value: "warm", text: "Chaleureux" },
            //         { value: "neon", text: "Néon" }
            //     ],
            //     prerequisites: [
            //         { key: "ui", value: "chart" }
            //     ]
            // },
            numeratorKey: {
                type: "select",
                label: "Numérateur",
                choices: ratioKeyChoices,
            },
            denominatorKey: {
                type: "select",
                label: "Dénominateur",
                choices: ratioKeyChoices,
            },
            nm1: {
                type: "boolean",
                label: "N-1",
                prerequisites: [{ key: "ui", value: ["table", "chart"] }],
            },
            // cumulative: {
            //     type: "boolean",
            //     label: "Cumulé",
            //     prerequisites: [
            //         { key: "ui", value: ["table", "chart"] }
            //     ]
            // },
            // dense: {
            //     type: "boolean",
            //     label: "Compact",
            //     prerequisites: [
            //         { key: "ui", value: ["table", "chart"] }
            //     ]
            // },
            // period: {
            //     type: "date-picker",
            //     unit: "month",
            //     label: "Période",
            //     min: "2024-01",
            //     max: "2024-12",
            //     prerequisites: [
            //         // { key: "ui", value: ["table", "chart"] }
            //     ]
            // },
            periodStart: {
                type: "select",
                label: "Date de début",
                choices: periodStartChoices,
                // prerequisites: [
                //     { key: "ui", value: ["table", "chart"] }
                // ]
            },
            periodEnd: {
                type: "select",
                label: "Date de fin",
                choices: periodEndChoices,
                // prerequisites: [
                //     { key: "ui", value: ["table", "chart"] }
                // ]
            },
        };
    }

    get reportPropertiesOptions(): any {
        const periodStartChoices = this.accountingPeriod
            ? getAccountingPeriodStartMonths(this.accountingPeriod)
            : [];
        const periodEndChoices = this.accountingPeriod
            ? getAccountingPeriodEndMonths(this.accountingPeriod)
            : [];
        return {
            title: {
                type: "text",
                label: "Titre de l'indicateur",
            },
            numeratorKey: {
                type: "select",
                label: "Numérateur",
                choices: [
                    { value: "K5M7dlWgTNheYB5E_70", text: "Chiffre d'affaires" },
                    { value: "K5M7dlWgTNheYB5E", text: "Chiffre d'affaires (sig)" },
                    { value: "HjWAbLWSgQTi5Wei", text: "Charges" },
                    { value: "GMmaP08MdztHyvBH", text: "Résultat" },
                    { value: "Cj3Em5e2ibqXknwb", text: "Achats consommés" },
                ],
            },
            denominatorKey: {
                type: "select",
                label: "Dénominateur",
                choices: [
                    { value: "K5M7dlWgTNheYB5E_70", text: "Chiffre d'affaires" },
                    { value: "K5M7dlWgTNheYB5E", text: "Chiffre d'affaires (sig)" },
                    { value: "HjWAbLWSgQTi5Wei", text: "Charges" },
                    { value: "GMmaP08MdztHyvBH", text: "Résultat" },
                    { value: "Cj3Em5e2ibqXknwb", text: "Achats consommés" },
                ],
            },
            periodStart: {
                type: "select",
                label: "Date de début",
                choices: periodStartChoices,
            },
            periodEnd: {
                type: "select",
                label: "Date de fin",
                choices: periodEndChoices,
            },
        };
    }

    constructor(
        layout: Layout,
        x: number,
        y: number,
        width: number,
        height: number,
        bobbeeCardTag,
        bobbeeCardProps,
        data = null,
        themeSettings: any = null,
        accountingPeriod: AccountingPeriod = null,
    ) {
        super(layout, x, y, width, height, bobbeeCardTag, bobbeeCardProps, data, themeSettings);
        // this.properties.indicator = "ratio"
        // this.properties.title = "Ratio"
        this.properties.ui = "chart";
        // this.properties.id = "ratio"
        this.properties.nm1 = false;
        // this.properties.period = []
        // this.properties.dense = false

        // delete this.properties.cumulative
        // delete this.propertiesOptions.cumulative

        // delete this.properties.dense
        // delete this.propertiesOptions.dense

        // delete this.properties.period
        // delete this.propertiesOptions.period

        this.data = {
            dataset: [[4, 1]],
            labels: ["N-1", "N"],
        };

        this.accountingPeriod = accountingPeriod;
    }

    get selectedData() {
        let header = ["N-1", "N"];
        let rows = this.data.dataset.map((row) => [...row]);
        if (!this.mergedProperties.nm1) {
            header.shift();
            rows = rows.map((row) => row.slice(1));
        }
        return {
            header: header,
            rows: rows,
        };
    }

    get tableData(): any {
        return {
            headers: ["", ...this.selectedData.header],
            rows: [["Ratio", ...this.selectedData.rows[0].map((x) => x + "%")]],
        };
    }

    get chartData() {
        const data: any = {
            labels: this.selectedData.header,
            datasets: [
                {
                    data: this.selectedData.rows[0],
                },
            ],
        };
        const chartType = this.selectedChartTheme ? this.selectedChartTheme.values.type : "bar";
        const colorPalette = this.selectedChartTheme
            ? this.selectedChartTheme.values.palette
            : "cool";
        chartColors(chartType, data.datasets, colorPalette);
        return data;
    }

    get chartOptions() {
        const options = {
            ...super.chartOptions,
            scales: {
                y: {
                    // type: "logarithmic",
                    stacked: false,
                    // min:0,
                    beginAtZero: true,
                    // stepSize: 1
                    // max: 100
                    suggestedMax: 100,
                },
                x: {
                    stacked: false,
                    // min:0,
                    beginAtZero: true,
                    // max: 100
                },
            },
            maintainAspectRatio: true,
        };
        return options;
    }
}

// OperatingAccountData: {
//     "id": "clesc5ora0022gism9uwft9lr",
//     "name": "Compte de résultat",
//     "type": "INCOME_STATEMENT",
//     "companyScope": {
//         "id": "clrc9k5bq0003smc89swqcqwm",
//         "name": "",
//         "accountingCompanyOwnerId": "ck93umlvcbtv60730gf5d8fkj",
//         "accountingCompanyOwner": {
//             "id": "ck93umlvcbtv60730gf5d8fkj",
//             "name": "OSS360"
//         },
//         "clientCount": 0,
//         "activityCount": 0,
//         "taxSystems": null,
//         "clients": [],
//         "activities": [],
//         "scopeSharingStatus": "VALIDATE",
//         "accountingCompanyId": null,
//         "accountingCompany": null
//     },
//     "accountableAccountFilter": "AA.name1 = '6' OR AA.name1 = '7'"
// }
export interface OperatingAccountDataInterface {
    id: string;
    name: string;
    type: string;
    companyScope?: any;
    accountableAccountFilter?: string;
}
export class AccountingResultsBoxModel extends IndicatorComponentBoxModel {
    componentName: string = "AccountingResults";
    minWidth = 500;
    minHeight = 300;
    accountingPeriod: AccountingPeriod = null;
    operatingAccountsData: OperatingAccountDataInterface[];
    friendlyTypename = "Tableau de résultats";

    constructor(
        layout: Layout,
        x: number,
        y: number,
        width: number,
        height: number,
        bobbeeCardTag,
        bobbeeCardProps,
        data = null,
        themeSettings: any = null,
        accountingPeriod: AccountingPeriod = null,
        operatingAccountsData: OperatingAccountDataInterface[] = [],
    ) {
        super(layout, x, y, width, height, bobbeeCardTag, bobbeeCardProps, data, themeSettings);
        this.data = { rows: [], headers: [] }; //dummyAccountingResultsData
        this.accountingPeriod = accountingPeriod;
        this.operatingAccountsData = operatingAccountsData;
    }

    // data_ = dummyAccountingResultsData

    properties = {
        // indicator: "results",
        // title: "Tableau",
        ui: "table",
        nGraphParts: 2,
        // tableType: "results",
        // flashAccount: "",
        // sigAccount: "",
        // bilanAccount: "",
        // resultsAccount: "",
        // // chartType: "bar",
        // // colorPalette: "cool",
        chartTheme: "default",
        tableTheme: "default",
        dense: false,
        nm1: false,
        // periodStart: "",
        // periodEnd:  ""
    };

    reportProperties: any = {
        title: "Tableau",
        tableType: "results",
        flashAccount: "",
        sigAccount: "",
        bilanAccount: "",
        resultsAccount: "",
        periodStart: "",
        periodEnd: "",
        selectedNames: [], // List of names in the data.rows for graph display,
    };

    get cardProps() {
        // :operatingAccountId="selectorOaFlashSituation"
        // :myKey="'Flash'"
        // :currentKey="step"
        // :options="options[step]"
        // :periods="periods"
        // :tags="tagIds"
        // :displayMonthlyData="displayMonthlyData"
        // :round="true"
        // :withSelector="OA_FlashSituation && OA_FlashSituation.length > 1"
        return {
            operatingAccountId: this.selectedOperatingAccount?.id,
            myKey: this.mergedProperties.tableType,
            currentKey: this.mergedProperties.tableType,
            options: this.propertiesOptions,
            periods: this.accountingPeriod,
            tags: this.mergedProperties.tableType === "Bilan" ? undefined : [],
            displayMonthlyData:
                this.mergedProperties.tableType === "Bilan" ? false : !this.mergedProperties.dense,
            round: true,
            withSelector: this.operatingAccountsData.length > 1,
        };
    }

    get resultsOperatingAccounts() {
        return this.operatingAccountsData.filter((account) => account.type === "INCOME_STATEMENT");
    }

    get flashOperatingAccounts() {
        return this.operatingAccountsData.filter((account) => account.type === "FLASH_SITUATION");
    }

    get SIGOperatingAccounts() {
        return this.operatingAccountsData.filter((account) => account.type === "SIG");
    }

    get bilanOperatingAccounts() {
        return this.operatingAccountsData.filter((account) => account.type === "BALANCE_SHEET");
    }

    get selectedOperatingAccount() {
        if (this.mergedProperties.tableType === "Flash") {
            return this.flashOperatingAccounts.find(
                (account) => account.id === this.mergedProperties.flashAccount,
            );
        } else if (this.mergedProperties.tableType === "SIG") {
            return this.SIGOperatingAccounts.find(
                (account) => account.id === this.mergedProperties.sigAccount,
            );
        } else if (this.mergedProperties.tableType === "Bilan") {
            return this.bilanOperatingAccounts.find(
                (account) => account.id === this.mergedProperties.bilanAccount,
            );
        } else if (this.mergedProperties.tableType === "results") {
            return this.resultsOperatingAccounts.find(
                (account) => account.id === this.mergedProperties.resultsAccount,
            );
        } else {
            return null;
        }
    }

    get propertiesOptions() {
        // const periodStartChoices = this.accountingPeriod ? getAccountingPeriodStartMonths(this.accountingPeriod) : []
        // const periodEndChoices = this.accountingPeriod ? getAccountingPeriodEndMonths(this.accountingPeriod) : []
        // // debugger
        // if (periodStartChoices.length && this.mergedProperties.periodStart === "") {
        //     this.reportProperties.periodStart = periodStartChoices[0].value
        // }
        // if (periodEndChoices.length && this.mergedProperties.periodEnd === "") {
        //     this.reportProperties.periodEnd = periodEndChoices[periodEndChoices.length - 1].value
        // }
        // // Set default values of selectors
        // if (this.mergedProperties.tableType === "Flash" && !this.mergedProperties.flashAccount && this.flashOperatingAccounts?.length) {
        //     this.reportProperties.flashAccount = this.flashOperatingAccounts[0].id
        // } else if (this.mergedProperties.tableType === "SIG" && !this.mergedProperties.sigAccount && this.SIGOperatingAccounts?.length) {
        //     this.reportProperties.sigAccount = this.SIGOperatingAccounts[0].id
        // } else if (this.mergedProperties.tableType === "Bilan" && !this.mergedProperties.bilanAccount && this.bilanOperatingAccounts?.length) {
        //     this.reportProperties.bilanAccount = this.bilanOperatingAccounts[0].id
        // } else if (this.mergedProperties.tableType === "results" && !this.mergedProperties.resultsAccount && this.resultsOperatingAccounts?.length) {
        //     this.reportProperties.resultsAccount = this.resultsOperatingAccounts[0].id
        // }

        if (this.mergedProperties.tableType === "Bilan") {
            this.properties.dense = true;
        }

        const uiChoices = [
            { value: "table", text: "Tableau" },
            { value: "card", text: "Carte" },
        ];
        if (this.mergedProperties.dense) {
            uiChoices.push({ value: "chart", text: "Graphe" });
        }

        return {
            title: {
                type: "text",
                label: "Titre de l'indicateur",
            },
            ui: {
                type: "select",
                label: "Type d'indicateur",
                choices: uiChoices,
            },
            nGraphParts: {
                type: "number",
                label: "Nombre d'éléments du graphe",
                min: 1,
                max: 8,
                prerequisites: [{ key: "ui", value: ["chart"] }],
            },
            // tableType: {
            //     type: "select",
            //     label: "Type de tableau",
            //     choices: [
            //         { value: "Flash", text: "Flash"},
            //         { value: "SIG", text: "SIG" },
            //         { value: "Bilan", text: "Bilan" },
            //         { value: "results", text: "Compte de résultat" },
            //     ],
            //     prerequisites: [
            //         { key: "ui", value: ["table", "card"] }
            //     ]
            // },
            // flashAccount: {
            //     type: "select",
            //     label: "Compte Flash",
            //     choices: this.flashOperatingAccounts.map(account => ({ value: account.id, text: account.name})),
            //     prerequisites: [
            //         { key: "ui", value: ["table", "card"] },
            //         { key: "tableType", value: "Flash" }
            //     ]
            // },
            // sigAccount: {
            //     type: "select",
            //     label: "Compte SIG",
            //     choices: this.SIGOperatingAccounts.map(account => ({ value: account.id, text: account.name})),
            //     prerequisites: [
            //         { key: "ui", value: ["table", "card"] },
            //         { key: "tableType", value: "SIG" }
            //     ]
            // },
            // bilanAccount: {
            //     type: "select",
            //     label: "Compte Bilan",
            //     choices: this.bilanOperatingAccounts.map(account => ({ value: account.id, text: account.name})),
            //     prerequisites: [
            //         { key: "ui", value: ["table", "card"] },
            //         { key: "tableType", value: "Bilan" }
            //     ]
            // },
            // resultsAccount: {
            //     type: "select",
            //     label: "Compte de résultat",
            //     choices: this.resultsOperatingAccounts.map(account => ({ value: account.id, text: account.name})),
            //     prerequisites: [
            //         { key: "ui", value: ["table", "card"] },
            //         { key: "tableType", value: "results" }
            //     ]
            // },

            // chartType: {
            //     type: "select",
            //     label: "Type de graphique",
            //     choices: [
            //         { value: "bar", text: "Barres" },
            //         { value: "line", text: "Lignes" },
            //         { value: "pie", text: "Secteurs" },
            //         { value: "doughnut", text: "Doughnut" }

            //     ],
            //     prerequisites: [
            // chartType: {
            //     type: "select",
            //     label: "Type de graphique",
            //     choices: [
            //         { value: "bar", text: "Barres" },
            //         { value: "line", text: "Lignes" },
            //         { value: "pie", text: "Secteurs" },
            //         { value: "doughnut", text: "Doughnut" }

            //     ],
            //     prerequisites: [
            //         { key: "ui", value: "chart" }
            //     ]
            // },
            chartTheme: {
                type: "select",
                label: "Thème des graphiques",
                choices:
                    this.themeSettings?.charts?.themesList?.map((theme) => ({
                        value: theme.id,
                        text: theme.displayName,
                    })) || [],
                prerequisites: [{ key: "ui", value: "chart" }],
            },
            tableTheme: {
                type: "select",
                label: "Thème des tableaux",
                choices:
                    this.themeSettings?.tables?.themesList?.map((theme) => ({
                        value: theme.id,
                        text: theme.displayName,
                    })) || [],
                prerequisites: [{ key: "ui", value: "table" }],
            },
            // colorPalette: {
            //     type: "select",
            //     label: "Palette de couleurs",
            //     choices: [
            //         { value: "cool", text: "Cool" },
            //         { value: "warm", text: "Chaleureux" },
            //         { value: "neon", text: "Néon" }
            //     ],
            //     prerequisites: [
            //         { key: "ui", value: "chart" }
            //     ]
            // },
            // id: {
            //     type: "text",
            //     label: "Chart ID"
            // },
            nm1: {
                type: "boolean",
                label: "N-1",
                prerequisites: [
                    // { key: "ui", value: ["table", "chart"] }
                ],
            },
            // cumulative: {
            //     type: "boolean",
            //     label: "Cumulé",
            //     prerequisites: [
            //         { key: "ui", value: ["table", "chart"] }
            //     ]
            // },
            dense: {
                type: "boolean",
                label: "Agrégée",
                prerequisites: [
                    // { key: "ui", value: ["table", "chart"] }
                ],
                // enableConditions: [
                //     { key: "tableType", value: ["Flash", "SIG", "results"] }
                // ]
            },
            // periodStart: {
            //     type: "select",
            //     label: "Date de début",
            //     choices: periodStartChoices
            //     // prerequisites: [
            //     //     { key: "ui", value: ["table", "chart"] }
            //     // ]
            // },
            // periodEnd: {
            //     type: "select",
            //     label: "Date de fin",
            //     choices: periodEndChoices
            //     // prerequisites: [
            //     //     { key: "ui", value: ["table", "chart"] }
            //     // ]
            // },
        };
    }

    get reportPropertiesOptions(): any {
        const periodStartChoices = this.accountingPeriod
            ? getAccountingPeriodStartMonths(this.accountingPeriod)
            : [];
        const periodEndChoices = this.accountingPeriod
            ? getAccountingPeriodEndMonths(this.accountingPeriod)
            : [];
        debugger;
        if (periodStartChoices.length && this.mergedProperties.periodStart === "") {
            this.reportProperties.periodStart = periodStartChoices[0].value;
        }
        if (periodEndChoices.length && this.mergedProperties.periodEnd === "") {
            this.reportProperties.periodEnd = periodEndChoices[periodEndChoices.length - 1].value;
        }
        // Set default values of selectors
        if (
            this.mergedProperties.tableType === "Flash" &&
            !this.mergedProperties.flashAccount &&
            this.flashOperatingAccounts?.length
        ) {
            this.reportProperties.flashAccount = this.flashOperatingAccounts[0].id;
        } else if (
            this.mergedProperties.tableType === "SIG" &&
            !this.mergedProperties.sigAccount &&
            this.SIGOperatingAccounts?.length
        ) {
            this.reportProperties.sigAccount = this.SIGOperatingAccounts[0].id;
        } else if (
            this.mergedProperties.tableType === "Bilan" &&
            !this.mergedProperties.bilanAccount &&
            this.bilanOperatingAccounts?.length
        ) {
            this.reportProperties.bilanAccount = this.bilanOperatingAccounts[0].id;
        } else if (
            this.mergedProperties.tableType === "results" &&
            !this.mergedProperties.resultsAccount &&
            this.resultsOperatingAccounts?.length
        ) {
            this.reportProperties.resultsAccount = this.resultsOperatingAccounts[0].id;
        }
        return {
            title: {
                type: "text",
                label: "Titre de l'indicateur",
            },
            tableType: {
                type: "select",
                label: "Type de tableau",
                choices: [
                    { value: "Flash", text: "Flash" },
                    { value: "SIG", text: "SIG" },
                    { value: "Bilan", text: "Bilan" },
                    { value: "results", text: "Compte de résultat" },
                ],
                prerequisites: [{ key: "ui", value: ["table", "card"] }],
            },
            flashAccount: {
                type: "select",
                label: "Compte Flash",
                choices: this.flashOperatingAccounts.map((account) => ({
                    value: account.id,
                    text: account.name,
                })),
                prerequisites: [
                    { key: "ui", value: ["table", "card"] },
                    { key: "tableType", value: "Flash" },
                ],
            },
            sigAccount: {
                type: "select",
                label: "Compte SIG",
                choices: this.SIGOperatingAccounts.map((account) => ({
                    value: account.id,
                    text: account.name,
                })),
                prerequisites: [
                    { key: "ui", value: ["table", "card"] },
                    { key: "tableType", value: "SIG" },
                ],
            },
            bilanAccount: {
                type: "select",
                label: "Compte Bilan",
                choices: this.bilanOperatingAccounts.map((account) => ({
                    value: account.id,
                    text: account.name,
                })),
                prerequisites: [
                    { key: "ui", value: ["table", "card"] },
                    { key: "tableType", value: "Bilan" },
                ],
            },
            resultsAccount: {
                type: "select",
                label: "Compte de résultat",
                choices: this.resultsOperatingAccounts.map((account) => ({
                    value: account.id,
                    text: account.name,
                })),
                prerequisites: [
                    { key: "ui", value: ["table", "card"] },
                    { key: "tableType", value: "results" },
                ],
            },
            periodStart: {
                type: "select",
                label: "Date de début",
                choices: periodStartChoices,
                // prerequisites: [
                //     { key: "ui", value: ["table", "chart"] }
                // ]
            },
            periodEnd: {
                type: "select",
                label: "Date de fin",
                choices: periodEndChoices,
                // prerequisites: [
                //     { key: "ui", value: ["table", "chart"] }
                // ]
            },
            selectedNames: {
                type: "select",
                label: "Postes",
                choices: this.names,
                multiple: true,
                visible: this.properties.ui === "chart",
                // prerequisites: [
                //     { key: "ui", value: ["table", "chart"] }
                // ]
            },
        };
    }

    get names() {
        return (
            this.data?.rows
                ?.filter((row) => row.name)
                .map((row) => ({ value: row.key, text: row.name })) ?? []
        );
    }

    get chartOptions() {
        return {
            ...super.chartOptions,
            plugins: {
                ...super.chartOptions.plugins,
                legend: {
                    display: true,
                    position: "bottom",
                },
            },
        };
    }

    selectTableDataByKeys(keys: any[]) {
        return this.data.rows.filter((row) => row.name && keys.includes(row.key));
    }
}

export class GridContainer extends ComponentBoxModel implements ContainerInterface {
    componentName: string = "GridBox";
    friendlyTypename = "Conteneur";
    // nRows: number = 4
    // nCols: number = 4
    children: Box[] = [];

    gridCells: GridCell[] = [];

    hideOverflowContent: boolean = false;

    constructor(
        layout: Layout,
        x: number,
        y: number,
        width: number,
        height: number,
        nRows: number,
        nCols: number,
        themeSettings?: any,
    ) {
        super(layout, x, y, width, height, themeSettings);
        this.nRows = nRows;
        this.nCols = nCols;

        // const testBox = new TitleBox(layout, 0, 0, 100, 100, themeSettings)
        // const testBox2 = new TitleBox(layout, 20, 20, 100, 100, themeSettings)
        // testBox.parentContainer = this
        // testBox2.parentContainer = this
        // testBox.properties.text = "1: " + testBox.id +  " - " + testBox.parentContainer?.id
        // testBox2.properties.text = "2: " + testBox2.id +  " - " + testBox2.parentContainer?.id
        // this.children.push(testBox, testBox2)

        this.buildGrid();
    }

    get allContainersMap(): Map<string, ContainerInterface> {
        const containers: ContainerInterface[] = [this];
        const visited = new Map<string, boolean>();

        // Depth first search to get all containers
        const dfs = (container: GridContainer | Layout) => {
            visited.set(container.id, true);
            containers.push(container);
            for (const child of container.children) {
                if (child instanceof GridContainer && !visited.has(child.id)) {
                    dfs(child);
                }
            }
        };
        dfs(this);
        return _.keyBy(containers, "id") as any;
    }

    get nRows() {
        return this.properties.nRows ?? 1;
    }

    set nRows(val: number) {
        this.properties.nRows = val;
        this.buildGrid();
    }

    get nCols() {
        return this.properties.nCols ?? 1;
    }

    set nCols(val: number) {
        this.properties.nCols = val;
        this.buildGrid();
    }

    properties = {
        // indicator: "results",
        nRows: 1,
        nCols: 1,
        matchCellWidth: true, // Cell child will attempt to match cell width (with padding)
        matchCellHeight: true, // Cell child will attempt to match cell height (with padding)
        overflowCell: false, // Cell child will overflow if needed. However, the min dimensions of the children have precedence.
        cellPadding: 0, // Cell padding in px
    };

    reportProperties: any = this.properties;

    get propertiesOptions(): any {
        return {
            nRows: {
                type: "number",
                label: "Nombre de lignes",
                min: 1,
            },
            nCols: {
                type: "number",
                label: "Nombre de colonnes",
                min: 1,
            },
            matchCellWidth: {
                type: "boolean",
                label: "Ajuster à la largeur des cellules",
            },
            matchCellHeight: {
                type: "boolean",
                label: "Ajuster à la hauteur des cellules",
            },
            overflowCell: {
                type: "boolean",
                label: "Déborder les cellures",
                prerequisites: [
                    {
                        clause: "OR",
                        conditions: [
                            { key: "matchCellWidth", value: false },
                            { key: "matchCellHeight", value: false },
                        ],
                    },
                ],
            },
            cellPadding: {
                type: "number",
                label: "Marge interne des cellules",
                // min: 0
            },
        };
    }

    get reportPropertiesOptions() {
        return {
            ...this.propertiesOptions,
        };
    }

    get cellPadding() {
        return this.properties.cellPadding ?? 0;
    }

    get allowCellsOverflow() {
        return (
            this.properties.matchCellWidth &&
            this.properties.matchCellHeight &&
            this.properties.overflowCell
        );
    }

    get offsetWithLayout(): { x: number; y: number } {
        return this.offsetsWithAncestor(this.layout);
    }

    addBox(box: Box): void {
        box.parentContainer = this;
        this.children.push(box);
    }

    public moveBoxToFront(box: Box) {
        if (box?.id in this.childrenMap) {
            this.children = this.children.filter((child) => child.id !== box.id);
            this.children.push(box);
        }
    }

    public moveBoxToBack(box: Box) {
        if (box?.id in this.childrenMap) {
            this.children = this.children.filter((child) => child.id !== box.id);
            this.children.unshift(box);
        }
    }

    public moveBoxForward(box: Box) {
        if (box?.id in this.childrenMap) {
            const index = this.children.findIndex((child) => child.id === box.id);
            if (index < this.children.length - 1) {
                this.children.splice(index, 1);
                this.children.splice(index + 1, 0, box);
            }
        }
    }

    public moveBoxBackward(box: Box) {
        if (box?.id in this.childrenMap) {
            const index = this.children.findIndex((child) => child.id === box.id);
            if (index > 0) {
                this.children.splice(index, 1);
                this.children.splice(index - 1, 0, box);
            }
        }
    }

    get gridCellsMap(): { [key: string]: GridCell } {
        return _.keyBy(this.gridCells, "id");
    }

    get childrenMap(): { [key: string]: Box } {
        return _.keyBy(this.children, "id");
    }

    // build(): void {

    // }

    getChildById(id: any) {
        return this.childrenMap?.[id];
    }

    getGridCelldById(id: any) {
        return this.gridCellsMap?.[id];
    }

    removeChildById(id: any) {
        // _.remove(this.children, { id })
        this.children = this.children.filter((child) => child.id !== id);
    }

    removeGridCellById(id: any) {
        // _.remove(this.gridCells, { id })
        this.gridCells = this.gridCells.filter((cell) => cell.id !== id);
    }

    get gridCellWidth() {
        let val = this.width;
        return parseFloat(val.toString()) / this.nCols;
    }

    get gridCellHeight() {
        let val = this.height;
        return parseFloat(val.toString()) / this.nRows;
    }

    /**
     * Build grid cells and resize children
     * If nCols * nRows < number of current cells, it will be truncated.
     * If nCols * nRows > number of current cells, it will be filled with new cells.
     * @param resizeBoxes
     */
    public buildGrid(/*resizeBoxes: boolean = false*/): void {
        const currentGridCells = [...this.gridCells];
        this.gridCells = [];
        let i = 0;
        for (let col = 0; col < this.nCols; col++) {
            const x = col * this.gridCellWidth;
            for (let row = 0; row < this.nRows; row++) {
                // const id = "cell_" + col + "_" + row
                const y = row * this.gridCellHeight;
                if (i < currentGridCells.length) {
                    currentGridCells[i].x = x;
                    currentGridCells[i].y = y;
                    currentGridCells[i].width = this.gridCellWidth;
                    currentGridCells[i].height = this.gridCellHeight;
                    this.gridCells.push(currentGridCells[i]);
                    const box = currentGridCells[i].box;
                    if (box) {
                        box.x = x + this.cellPadding;
                        box.y = y + this.cellPadding;
                        if (this.properties.matchCellWidth) {
                            box.width = this.gridCellWidth - 2 * this.cellPadding;
                        } else {
                            if (!this.properties.overflowCell) {
                                const newWidth = Math.min(
                                    box.width,
                                    this.gridCellWidth - 2 * this.cellPadding,
                                );
                                box.width = newWidth;
                            }
                        }
                        if (this.properties.matchCellHeight) {
                            box.height = this.gridCellHeight - 2 * this.cellPadding;
                        } else {
                            if (!this.properties.overflowCell) {
                                const newHeight = Math.min(
                                    box.height,
                                    this.gridCellHeight - 2 * this.cellPadding,
                                );
                                box.height = newHeight;
                            }
                        }
                        // box.width = this.gridCellWidth - 20
                        // box.height = this.gridCellHeight - 20
                        if (box instanceof GridContainer) {
                            box.buildGrid(/*resizeBoxes*/);
                        }
                    }
                    i++;
                } else {
                    this.gridCells.push(
                        new GridCell(x, y, this.gridCellWidth, this.gridCellHeight, this),
                    );
                }
            }
        }
        if (i < currentGridCells.length) {
            // If there are truncated cells with boxes, remove the boxes from the container and put them in the layout
            currentGridCells.slice(i).forEach((cell) => {
                if (cell.box) {
                    cell.box.cell = null;
                    cell.box.x =
                        /*(cell.box.parentContainer?.offsetWithLayout?.x ?? 0) +*/ cell.box.x +
                        cell.box.offsetWithLayout.x +
                        20;
                    cell.box.y =
                        /*(cell.box.parentContainer?.offsetWithLayout?.y ?? 0) +*/ cell.box.y +
                        cell.box.offsetWithLayout.y +
                        20;
                    cell.box.parentContainer = this.layout;
                    this.layout.addBox(cell.box);
                    this.removeChildById(cell.box.id);
                    cell.box = null;
                }
            });
        }
        // Update previous coordinates of container and children to keep consistency
        this.updatePreviousCoordinates();
        this.children.forEach((box) => box.updatePreviousCoordinates());
    }

    /**
     * Build grid cells and resize children
     */
    public build(): void {
        this.buildGrid();
    }

    public resizeBoxes() {
        this.gridCells.forEach((cell) => {
            if (cell.box) {
                cell.box.x = cell.x + this.cellPadding;
                cell.box.y = cell.y + +this.cellPadding;
                cell.box.width = cell.width - 2 * this.cellPadding;
                cell.box.height = cell.height - 2 * this.cellPadding;
                if (cell.box instanceof GridContainer) {
                    cell.box.resizeBoxes();
                }
            }
        });
    }

    public setWidth(width: number) {
        this.width = width;
        this.buildGrid();
    }

    public setHeight(height: number) {
        this.height = height;
        this.buildGrid();
    }

    public setWidthAndHeight(width: number, height: number) {
        this.width = width;
        this.height = height;
        this.buildGrid();
    }
}

export class SimpleContainer extends GridContainer {
    componentName: string = "GridBox";
    friendlyTypename = "Conteneur simple";

    constructor(
        layout: Layout,
        x: number,
        y: number,
        width: number,
        height: number,
        themeSettings?: any,
    ) {
        super(layout, x, y, width, height, 1, 1, themeSettings);
        this.properties.nRows = 1;
        this.properties.nCols = 1;
    }

    properties = {
        // indicator: "results",
        nRows: 1,
        nCols: 1,
        matchCellWidth: false, // Cell child will attempt to match cell width (with padding)
        matchCellHeight: false, // Cell child will attempt to match cell height (with padding)
        overflowCell: false, // Cell child will overflow if needed. However, the min dimensions of the children have precedence.
        keepChildrenWidthRatio: false, // While resizing, it will attempt to keep child width to container width ratio
        keepChildrenHeightRatio: false, // While resizing, it will attempt to keep child height to container height ratio
        childrenMatchWidth: false,
        cellPadding: 0, // Cell padding in px
    };

    reportProperties: any = this.properties;

    get propertiesOptions() {
        return {
            nRows: {
                type: "number",
                label: "Nombre de lignes",
                min: 1,
                visible: false,
            },
            nCols: {
                type: "number",
                label: "Nombre de colonnes",
                min: 1,
                visible: false,
            },
            matchCellWidth: {
                visible: false,
            },
            matchCellHeight: {
                visible: false,
            },
            overflowCell: {
                visible: false,
            },
            keepChildrenWidthRatio: {
                type: "boolean",
                label: "Conserver le ratio horizontal des composants",
                enableConditions: [{ key: "childrenMatchWidth", value: false }],
            },
            keepChildrenHeightRatio: {
                type: "boolean",
                label: "Conserver le ratio vertical des composants",
            },
            childrenMatchWidth: {
                type: "boolean",
                label: "Ajuster la largeur des composants au conteneur",
            },
            cellPadding: {
                type: "number",
                label: "Marge interne des cellules",
                // min: 0,
                prerequisites: [{ key: "childrenMatchWidth", value: true }],
            },
        };
    }

    get reportPropertiesOptions() {
        return {
            ...this.propertiesOptions,
        };
    }

    get keepChildrenWidthRatio(): boolean {
        return this.properties.keepChildrenWidthRatio;
    }

    get keepChildrenHeightRatio(): boolean {
        return this.properties.keepChildrenHeightRatio;
    }

    get childrenMatchWidth(): boolean {
        return this.properties.childrenMatchWidth;
    }

    /**
     * Build container and grids and resize children dimensions with respect to the container settings.
     * Recursively call buildGrid() on children containers.
     */
    public buildGrid(): void {
        for (const child of this.children) {
            const widthChangeRatio = this.width / this.previousWidth;
            const heightChangeRatio = this.height / this.previousHeight;
            const childWToParentWRation = 1;
            if (this.keepChildrenWidthRatio) {
                child.width = child.width * widthChangeRatio;
                child.x = child.x * widthChangeRatio; // child x is relative to parent x, and is basically the width from the origin to the point
            }
            if (this.keepChildrenHeightRatio) {
                child.height = child.height * heightChangeRatio;
                child.y = child.y * heightChangeRatio; // child y is relative to parent y, and is basically the height from the origin to the point
            }
            if (this.childrenMatchWidth) {
                child.width = this.width - 2 * this.properties.cellPadding;
                child.x = this.properties.cellPadding;
            }

            if (child instanceof GridContainer) {
                child.buildGrid();
            }
            child.updatePreviousCoordinates();
        }
        this.updatePreviousCoordinates();
    }
}

export enum ReportType {
    PDF = "PDF",
    DASHBOARD = "Dashboard",
    SLIDEVIEW = "Slides",
}

const SLIDE_DIMENSIONS_IN_PX = {
    width: 960,
    height: 540,
};

const A4_DIMENSIONS_IN_PX = {
    width: 794,
    height: 1123,
};

const DASHBOARD_DIMENSIONS_IN_PX = {
    width: 1920,
    height: 1080,
};

const REPORT_TYPES_DIMS_MAP = {
    [ReportType.PDF]: A4_DIMENSIONS_IN_PX,
    [ReportType.DASHBOARD]: DASHBOARD_DIMENSIONS_IN_PX,
    [ReportType.SLIDEVIEW]: SLIDE_DIMENSIONS_IN_PX,
};

export enum ReportElementDisposition {
    VERTICAL = "vertical",
    HORIZONTAL = "horizontal",
}

export class ReportElement extends SimpleContainer {
    // TODO: continue logic
    parent: Report = null;
    declare id: string;
    declare x: number;
    declare y: number;
    // width: number
    // height: number

    constructor() {
        super(null, 0, 0, 0, 0);
        this.id = this._generateId();
    }

    // _generateId(): string {
    //     return "repel_" + Math.random().toString(36).substring(2, 24)
    // }
}

export class Report {
    // TODO: continue logic
    id: string;
    type: ReportType;
    name: string;
    description: string;
    themeSettings: any;
    createdAt: Date;
    updatedAt: Date;
    elementsDisposition: ReportElementDisposition = ReportElementDisposition.VERTICAL;

    elements: ReportElement[] = [];

    constructor(type: ReportType, name: string, description: string, themeSettings: any = null) {
        this.id = this._generateId();
        this.type = type;
        this.name = name;
        this.description = description;
        this.themeSettings = themeSettings;
        this.createdAt = new Date();
        this.updatedAt = new Date();
    }

    get elementsMap(): { [id: string]: ReportElement } {
        return _.keyBy(this.elements, "id");
        // return this.elements.reduce((acc, el) => {
        //     acc[el.id] = el
        //     return acc
        // }, {})
    }

    get elementById(): (id: string) => ReportElement {
        return (id: string) => this.elementsMap[id];
    }

    get elementWidth(): number {
        return REPORT_TYPES_DIMS_MAP[this.type].width;
    }

    get elementHeight(): number {
        return REPORT_TYPES_DIMS_MAP[this.type].height;
    }

    _generateId(): string {
        return "rep_" + Math.random().toString(36).substring(2, 24);
    }

    addElement(element: ReportElement): void {
        if (element) {
            element.parent = this;
            element.width = this.elementWidth;
            element.height = this.elementHeight;
            // element.x = 50
            // element.y = 50
            this.elements.push(element);
        }
    }
}
